import { useEffect } from 'react';

/**
 * The boot splash is the `#boot-splash` overlay declared in index.html: it is
 * on screen from the very first paint (before React, before the auth check)
 * so an installed app opens on the same dark screen + icon as the native
 * splash, with a breathing copper glow, instead of a blank page and a
 * spinner. It stays up until the first screen can be shown whole: App.jsx
 * hides it once the auth state is known, except on student screens, which
 * hide it themselves (useBootSplashReady) once their data has landed — so the
 * student never sees a second loader after the logo. The overlay fades out
 * over its CSS transition and is then removed. index.html also hides it by
 * itself after a few seconds, in case the app never gets that far.
 */
export const BOOT_SPLASH_ID = 'boot-splash';
const HIDDEN_CLASS = 'is-hidden';
// A little longer than the CSS transition (450ms), so the node is gone only
// once the fade has finished.
const REMOVE_AFTER_MS = 600;

export function hideBootSplash() {
  if (typeof document === 'undefined') return;
  const splash = document.getElementById(BOOT_SPLASH_ID);
  if (!splash || splash.classList.contains(HIDDEN_CLASS)) return;
  splash.classList.add(HIDDEN_CLASS);
  window.setTimeout(() => splash.remove(), REMOVE_AFTER_MS);
}

// Screens a signed-in student can open on: the root (which redirects to the
// dashboard), the chat and every /student/* page. Each of them calls
// useBootSplashReady, so App.jsx leaves the splash to them.
export function isStudentBootScreen(pathname) {
  return pathname === '/' || pathname === '/chat' || pathname.startsWith('/student/');
}

// Hides the splash once `ready` turns true. A no-op after the first time
// (the splash is gone), so later loads of the page never touch it.
export function useBootSplashReady(ready) {
  useEffect(() => {
    if (ready) hideBootSplash();
  }, [ready]);
}
